// Voice utilities for speech synthesis and recognition
// These only work in the browser, hence the .client.ts extension

// Type definitions for SpeechRecognition API
interface SpeechRecognitionEvent extends Event {
	results: SpeechRecognitionResultList
	resultIndex: number
}

interface SpeechRecognitionResultList {
	length: number
	item(index: number): SpeechRecognitionResult
	[index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
	isFinal: boolean
	length: number
	item(index: number): SpeechRecognitionAlternative
	[index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionAlternative {
	transcript: string
	confidence: number
}

interface SpeechRecognition extends EventTarget {
	lang: string
	continuous: boolean
	interimResults: boolean
	maxAlternatives: number
	start(): void
	stop(): void
	abort(): void
	onresult: ((event: SpeechRecognitionEvent) => void) | null
	onerror: ((event: Event) => void) | null
	onend: (() => void) | null
}

interface SpeechRecognitionConstructor {
	new (): SpeechRecognition
}

declare global {
	interface Window {
		SpeechRecognition?: SpeechRecognitionConstructor
		webkitSpeechRecognition?: SpeechRecognitionConstructor
	}
}

export class VoiceAssistant {
	private synthesis: SpeechSynthesis | null = null
	private recognition: SpeechRecognition | null = null
	private dutchVoice: SpeechSynthesisVoice | null = null

	constructor() {
		if (typeof window !== 'undefined') {
			this.synthesis = window.speechSynthesis

			// Initialize speech recognition
			const SpeechRecognition =
				window.SpeechRecognition || window.webkitSpeechRecognition
			if (SpeechRecognition) {
				// console.log('SpeechRecognition API available')
				try {
					this.recognition = new SpeechRecognition()
					this.recognition.lang = 'nl-NL' // Dutch
					this.recognition.continuous = false
					this.recognition.interimResults = true
					this.recognition.maxAlternatives = 1
					// console.log('Speech recognition initialized with lang:', this.recognition.lang)
				} catch {
					// console.error('Failed to initialize speech recognition:', error)
					this.recognition = null
				}
			} else {
				// console.log('SpeechRecognition API not available')
			}

			// Load voices and find Dutch voice
			this.loadVoices()
			if (this.synthesis) {
				this.synthesis.addEventListener('voiceschanged', () =>
					this.loadVoices(),
				)
			}
		}
	}

	private loadVoices() {
		if (!this.synthesis) return

		const voices = this.synthesis.getVoices()
		// Try to find a Dutch voice, prefer female voices for Else
		this.dutchVoice =
			voices.find(
				(voice) =>
					voice.lang.startsWith('nl') &&
					voice.name.toLowerCase().includes('female'),
			) ||
			voices.find((voice) => voice.lang.startsWith('nl')) ||
			null

		// console.log('Available Dutch voices:', voices.filter(v => v.lang.startsWith('nl')))
		// console.log('Selected voice:', this.dutchVoice)
	}

	speak(text: string, onStart?: () => void, onEnd?: () => void): void {
		if (!this.synthesis) {
			console.warn('Speech synthesis not available')
			return
		}

		// Cancel any ongoing speech
		this.synthesis.cancel()

		const utterance = new SpeechSynthesisUtterance(text)
		utterance.lang = 'nl-NL'
		utterance.rate = 0.9 // Slightly slower for clarity
		utterance.pitch = 1.1 // Slightly higher pitch

		if (this.dutchVoice) {
			utterance.voice = this.dutchVoice
		}

		if (onStart) {
			utterance.addEventListener('start', onStart)
		}

		if (onEnd) {
			utterance.addEventListener('end', onEnd)
			utterance.addEventListener('error', onEnd) // Also call onEnd on error
		}

		this.synthesis.speak(utterance)
	}

	stopSpeaking(): void {
		if (this.synthesis) {
			this.synthesis.cancel()
		}
	}

	startListening(
		onResult: (transcript: string, isFinal: boolean) => void,
		onError?: (error: Event) => void,
		onEnd?: () => void,
	): boolean {
		if (!this.recognition) {
			console.warn('Speech recognition not available')
			return false
		}

		// Reset the recognition object to clear any previous state
		this.recognition.onresult = null
		this.recognition.onerror = null
		this.recognition.onend = null

		this.recognition.onresult = (event: SpeechRecognitionEvent) => {
			// console.log('Speech recognition result event:', event)
			const last = event.results.length - 1
			const transcript = event.results[last]?.[0]?.transcript || ''
			const isFinal = event.results[last]?.isFinal || false
			// console.log('Transcript:', transcript, 'isFinal:', isFinal)
			onResult(transcript, isFinal)
		}

		this.recognition.onerror = (event: any) => {
			// console.error('Speech recognition error:', {
			// 	error: event.error,
			// 	message: event.message,
			// 	type: event.type,
			// 	timeStamp: event.timeStamp
			// })
			if (onError) onError(event)
		}

		this.recognition.onend = () => {
			// console.log('Speech recognition ended')
			if (onEnd) onEnd()
		}

		try {
			// console.log('Starting speech recognition with lang:', this.recognition.lang)
			// console.log('Page protocol:', window.location.protocol)
			// console.log('Is secure context:', window.isSecureContext)

			// Check if we're in a secure context (required for speech recognition)
			if (!window.isSecureContext && window.location.hostname !== 'localhost') {
				// console.error('Speech recognition requires HTTPS or localhost')
				return false
			}

			this.recognition.start()
			return true
		} catch {
			// console.error('Failed to start speech recognition:', error)
			return false
		}
	}

	stopListening(): void {
		if (this.recognition) {
			try {
				// console.log('Stopping speech recognition')
				this.recognition.abort() // Use abort instead of stop to ensure it stops
			} catch {
				// console.log('Error stopping recognition (might already be stopped):', error)
				// Ignore errors when stopping
			}
		}
	}

	isAvailable(): { synthesis: boolean; recognition: boolean } {
		return {
			synthesis: !!this.synthesis,
			recognition: !!this.recognition,
		}
	}
}

// Singleton instance
let voiceAssistant: VoiceAssistant | null = null

export function getVoiceAssistant(): VoiceAssistant {
	if (!voiceAssistant) {
		voiceAssistant = new VoiceAssistant()
	}
	return voiceAssistant
}
